import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { AddonDrafts } from "./AddonDrafts";
import { Package, Check, X, Receipt } from "lucide-react";

export function AddonRequests() {
  const [data, setData] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/addon-requests").then((r) => setData(r.data)).catch(() => setData([]));
  useEffect(() => { load(); }, []);

  const setAmt = (rid, name, val) => setAmounts((a) => ({ ...a, [`${rid}:${name}`]: val }));
  const amtFor = (r, item) => {
    const key = `${r.id}:${item.name}`;
    if (amounts[key] !== undefined) return amounts[key];
    return item.variable ? "" : String(item.suggested_amount || "");
  };

  const total = (r) => (r.addons || []).reduce((s, i) => s + (parseFloat(amtFor(r, i)) || 0), 0);

  const approve = async (r) => {
    const line_items = (r.addons || []).map((i) => ({ name: i.name, amount: parseFloat(amtFor(r, i)) || 0 }));
    if (line_items.every((l) => l.amount <= 0)) { toast.error("Set an amount for at least one item."); return; }
    setBusy(r.id);
    try {
      const { data: res } = await api.post(`/admin/addon-requests/${r.id}/approve`, { line_items });
      toast.success(`Invoice ${res.invoice.number} issued — $${res.invoice.amount.toLocaleString()}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); } finally { setBusy(null); }
  };
  const decline = async (r) => {
    setBusy(r.id);
    try { await api.post(`/admin/addon-requests/${r.id}/decline`); toast.success("Request declined"); load(); }
    catch { toast.error("Could not decline"); } finally { setBusy(null); }
  };

  if (!data) return <Loader />;
  const pending = data.filter((r) => r.status === "requested");
  const closed = data.filter((r) => r.status !== "requested");

  return (
    <div data-testid="admin-addon-requests">
      <Head title="Add-on Requests" desc="Clients requesting upgrades on their projects. Confirm amounts and approve to issue an invoice." />
      {data.length === 0 ? (
        <EmptyBox text="No add-on requests yet. When a client requests upgrades from their portal, they'll appear here." />
      ) : (
        <div className="space-y-8">
          {pending.length > 0 && (
            <div className="space-y-4">
              <div className="text-[11px] uppercase tracking-wider text-gold">Awaiting your review ({pending.length})</div>
              {pending.map((r) => (
                <div key={r.id} className="bg-navy-800 border border-gold/25 p-5" data-testid={`addon-request-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-gold" />
                      <span className="text-white font-medium">{r.org_name || r.email}</span>
                      <span className="text-xs text-gray-500">{r.email}</span>
                    </div>
                    <span className="text-xs text-gray-500">{timeAgo(r.created_at)}</span>
                  </div>
                  {r.note && <p className="text-sm text-gray-400 mb-3 italic">"{r.note}"</p>}
                  <div className="space-y-2 mb-4">
                    {(r.addons || []).map((item) => (
                      <div key={item.name} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-200">{item.name}{item.variable && <span className="text-[11px] text-amber-400 ml-2">quote required</span>}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">$</span>
                          <input type="number" min="0" value={amtFor(r, item)} onChange={(e) => setAmt(r.id, item.name, e.target.value)}
                            placeholder={item.variable ? "quote" : ""} data-testid={`addon-amount-${r.id}-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="w-24 bg-navy border border-white/20 text-white px-2 py-1.5 text-sm text-right focus:border-gold focus:outline-none" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
                    <span className="text-sm text-gray-400">Invoice total: <span className="text-white font-semibold">${total(r).toLocaleString()}</span></span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => decline(r)} disabled={busy === r.id} data-testid={`addon-decline-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-white border border-white/15"><X size={14} /> Decline</button>
                      <button onClick={() => approve(r)} disabled={busy === r.id} data-testid={`addon-approve-${r.id}`}
                        className="inline-flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                        <Receipt size={14} /> Approve & invoice
                      </button>
                    </div>
                  </div>
                  <AddonDrafts projectId={r.project_id} addons={r.addons} />
                </div>
              ))}
            </div>
          )}
          {closed.length > 0 && (
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-gray-500">History ({closed.length})</div>
              {closed.map((r) => (
                <div key={r.id} className="bg-navy-800 border border-white/10 p-4" data-testid={`addon-request-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm text-gray-200">{r.org_name || r.email}</div>
                      <div className="text-xs text-gray-500">{(r.addons || []).map((a) => a.name).join(", ")}</div>
                    </div>
                    <span className={`text-xs px-2.5 py-0.5 border capitalize ${r.status === "approved" ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"}`}>
                      {r.status}{r.invoice_number ? ` · ${r.invoice_number}` : ""}
                    </span>
                  </div>
                  {r.status === "approved" && <AddonDrafts projectId={r.project_id} addons={r.addons} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
